import {
    AppButton,
    AppCompoundButton,
    AppFlyoutAnchor,
    AppSearchBox,
    AppSplitButton,
    AppToggleButton,
    getRibbonIconColor,
} from '../index';
import { getIsMsHighContrast } from 'owa-high-contrast';
import type { IButtonStyles } from '@fluentui/react';
import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import type { AppCompoundButtonProps } from '@1js/acui-button/lib/components/AppCompoundButton';
import type { MenuDefinition } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import type {
    RibbonButtonProps,
    RibbonFlyoutAnchorProps,
    RibbonSplitButtonProps,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import type { AppSearchBoxProps } from '@1js/acui-search-box/lib/components/AppSearchBox';

/**
 * In order to make High Contrast more-or-less work, we need to remove all styling from our ribbon buttons.
 * If we are NOT in HC, we'll use any styling passed in, or fall back to default styles for the current
 * ribbon mode.
 */
// Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
// -> Error TS7006 (32,29): Parameter 'styles' implicitly has an 'any' type.
// @ts-expect-error
function getAppButtonStyles(styles?): any {
    if (getIsMsHighContrast()) {
        return undefined;
    }

    if (styles) {
        return styles;
    }
    return undefined;
}

/**
 * Returns the color that should be used for a given control's
 * icon. In order of priority:
 * 0. Outside the purview of this method, `overrides` parameter provided to createAppButton/createAppMenu/etc.
 * will be applied over the value returned from this method.
 * 1. If we're in high contrast mode, appropriate high contrast colors will be applied. This functionality is
 * provided by default by @1js/acui-ribbon-like when iconColor and style arguments are undefined.
 * 2. An explicit `iconColor` parameter provided to createAppButton/createAppMenuItem (or similar). Note this
 * value is used for all interactive states, so consider contrast requirements for rest, hover, pressed, etc.
 * across all themes (high contrast black, high contrast white, dark mode, light mode).
 * 3. Create and register an iconColorMap with initializeIconColorMap containing an entry for your control's `id`
 * @param id
 * @param iconColor
 */
function getIconColor(id?: string | number, iconColor?: string): string | undefined {
    if (getIsMsHighContrast()) {
        return undefined;
    }

    if (iconColor !== undefined) {
        return iconColor;
    }

    if (id) {
        if (typeof id === 'number') {
            return getRibbonIconColor(id);
        } else {
            // Be able to pull an icon color for buttons that are instanced in multiple ribbons, and
            // have distinct names of the form '<RibbonId>_<unique identifier>'. parseInt() will
            // turn these into the number <RibbonId>.
            const convertedNum: number = parseInt(id);
            if (!isNaN(convertedNum)) {
                return getRibbonIconColor(convertedNum);
            }
        }
    }

    return undefined;
}

/**
 * creates RibbonButtonProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createAppButton = (
    id: string | number,
    label: string,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (105,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonButtonProps => {
    return {
        type: AppButton,
        id: id.toString(),
        label,
        icon, // Used for buttons within a Flyout menu
        iconSet: { default: icon }, // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: getIconColor(id, iconColor),
        styles: getAppButtonStyles(styles),
        keytipProps,
        onExecute,
        ...overrides,
    };
};

/**
 * creates RibbonButtonProps object for use with the WAC Ribbon package. Button handles selected state.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param toggled Whether the button should be selected
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createAppToggleButton = (
    id: number | string,
    label: string,
    onExecute: () => void,
    toggled: boolean,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (145,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonButtonProps => {
    return {
        type: AppToggleButton,
        id: id.toString(),
        label,
        icon, // Used for buttons within a Flyout menu
        iconSet: { default: icon }, // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: iconColor ?? getRibbonIconColor(id),
        styles: getAppButtonStyles(styles),
        onExecute,
        toggled,
        keytipProps,
        ...overrides,
    };
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createAppMenuItem = (
    id: string | number,
    label: string,
    onExecute: () => void,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (182,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): MenuItemControlProps => {
    return {
        type: AppButton,
        id: id.toString(),
        label,
        icon, // Used for buttons within a Flyout menu
        iconSet: { default: icon }, // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: getIconColor(id, iconColor),
        styles: getAppButtonStyles(styles),
        onExecute,
        ...overrides,
    };
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package. Used for generated menu items (i.e. from a custom list of categories)
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for menu item
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param customTooltip Optional hover tooltip
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createGeneratedMenuItem = (
    id: number | string,
    label: string,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    customTooltip?: string,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (221,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): MenuItemControlProps => {
    return {
        type: AppButton,
        id,
        label,
        icon, // Used for buttons within a Flyout menu
        iconColor: getIconColor(undefined /* id for icon color map */, iconColor),
        styles: getAppButtonStyles(styles),
        customTooltip,
        onExecute,
        keytipProps,
        ...overrides,
    };
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package. Used for generated menu items (i.e. from a custom list of categories). Button handles selected state
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param toggled Whether the button should be selected
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createGeneratedToggleMenuItem = (
    id: string,
    label: string,
    onExecute: () => void,
    toggled: boolean,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (259,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): MenuItemControlProps => {
    return {
        type: AppToggleButton,
        id: id,
        label,
        icon, // Used for buttons within a Flyout menu
        iconColor: iconColor,
        styles: getAppButtonStyles(styles),
        onExecute,
        toggled,
        ...overrides,
    };
};

/**
 * Creates RibbonFlyoutAnchorProps object for use with the WAC Ribbon package.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param toggled Whether the button should be selected
 * @param keytipProps Optional keytip for menu item
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createAppFlyoutAnchor = (
    id: string | number,
    label: string,
    menuDefinition: MenuDefinition,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (297,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonFlyoutAnchorProps => {
    return {
        type: AppFlyoutAnchor,
        id: id.toString(),
        label,
        keytipProps,
        icon,
        iconSet: { default: icon },
        iconColor: getIconColor(id, iconColor),
        styles: getAppButtonStyles(styles),
        menuDefinition,
        ...overrides,
    };
};

/**
 * creates RibbonSplitButtonProps object for use with the WAC Ribbon package.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for menu item
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createAppSplitButton = (
    id: number | string,
    label: string,
    menuDefinition: MenuDefinition,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (337,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonSplitButtonProps => {
    return {
        type: AppSplitButton,
        id: id.toString(),
        label,
        iconSet: { default: icon },
        iconColor: getIconColor(id, iconColor),
        onExecute,
        keytipProps,
        styles: getAppButtonStyles(styles),
        menuDefinition,
        ...overrides,
    };
};

/**
 * creates AppCompoundButtonProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param description Secondary information displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param customTooltip Optional hover tooltip
 * @param overrides Optional object/array of other AppCompoundButtonProps props to override.
 */
export const createAppCompoundButton = (
    id: number | string,
    label: string,
    description: string,
    onExecute: () => void,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    customTooltip?: string,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (377,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): AppCompoundButtonProps => {
    return {
        type: AppCompoundButton,
        id: id.toString(),
        label,
        description,
        icon, // Used for buttons within a Flyout menu
        iconSet: { default: icon }, // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: getIconColor(id, iconColor),
        onExecute,
        styles,
        customTooltip,
        ...overrides,
    };
};

/**
 * creates AppSearchBoxProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onChangeCallback Callback when the searchbox contents change. Empty box returns "undefined" newValue
 * @param keytipProps Optional keytip for search box
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param overrides Optional object/array of other AppSearchBoxProps props to override.
 */
export const createAppSearchBox = (
    id: number | string,
    label: string,
    onChangeCallback: (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (414,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): AppSearchBoxProps => {
    return {
        type: AppSearchBox,
        id: id.toString(),
        label,
        onChange: onChangeCallback,
        keytipProps,
        icon,
        iconColor: getIconColor(id, iconColor),
        ...overrides,
    };
};
